import React, { forwardRef, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

type InputSize = "xs" | "sm" | "md";
type InputVariant = "table" | "outlined";

const INPUT_SIZE_CLASSES: Record<InputSize, string> = {
    xs: "py-0.5 text-xs",
    sm: "py-1   text-xs",
    md: "py-1.5 text-sm",
};

interface AutocompleteInputProps {
    value: string;
    onChange: (val: string) => void;
    options: (string | { label: string; value: string })[];
    placeholder?: string;
    onEnterNext?: () => void;
    className?: string;
    dropdownMinWidth?: number;
    showAllOnFocus?: boolean;
    inputSize?: InputSize;
    /** "table" (default) — transparent, no border; "outlined" — bordered with bg for standalone use */
    variant?: InputVariant;
    containerClassName?: string;
}

const AutocompleteInput = forwardRef<HTMLInputElement, AutocompleteInputProps>(
    ({
         value,
         onChange,
         options,
         placeholder = "",
         onEnterNext,
         className,
         dropdownMinWidth = 180,
         showAllOnFocus = false,
         inputSize = "xs",
         variant = "table",
         containerClassName,
     }, ref) => {
        const [isOpen, setIsOpen]               = useState(false);
        const [isFocused, setIsFocused]         = useState(false);
        const [highlightedIndex, setHighlightedIndex] = useState(0);

        const containerRef = useRef<HTMLDivElement>(null);
        const dropdownRef  = useRef<HTMLDivElement>(null);
        const optionRefs   = useRef<(HTMLDivElement | null)[]>([]);

        const filtered = options
            .map((opt) => (typeof opt === "string" ? opt : opt.label))
            .filter((opt) => {
                if (showAllOnFocus && !value.trim()) return true;
                return opt.toLowerCase().includes(value.toLowerCase());
            })
            .slice(0, 8);

        useEffect(() => { setHighlightedIndex(0); }, [value]);

        useEffect(() => {
            if (isOpen && optionRefs.current[highlightedIndex] && dropdownRef.current) {
                const el       = optionRefs.current[highlightedIndex];
                const dropdown = dropdownRef.current;
                if (el) {
                    if (el.offsetTop < dropdown.scrollTop)
                        dropdown.scrollTop = el.offsetTop;
                    else if (el.offsetTop + el.offsetHeight > dropdown.scrollTop + dropdown.clientHeight)
                        dropdown.scrollTop = el.offsetTop + el.offsetHeight - dropdown.clientHeight;
                }
            }
        }, [highlightedIndex, isOpen]);

        useEffect(() => {
            optionRefs.current = optionRefs.current.slice(0, filtered.length);
            if (highlightedIndex >= filtered.length && filtered.length > 0)
                setHighlightedIndex(filtered.length - 1);
        }, [filtered.length, highlightedIndex]);

        const getDropdownStyle = (): React.CSSProperties => {
            if (!containerRef.current) return {};
            const rect = containerRef.current.getBoundingClientRect();
            const minWidth = Math.max(rect.width, dropdownMinWidth);
            const wouldOverflowRight = rect.left + minWidth > window.innerWidth - 20;
            const dropdownHeight = Math.min(filtered.length * 36 + 8, 200);
            const wouldOverflowBottom = rect.bottom + dropdownHeight > window.innerHeight - 20;
            const maxWidth = window.innerWidth - rect.left - 20;
            return {
                position: "fixed",
                minWidth: `${minWidth}px`,
                maxWidth: `${Math.max(minWidth, maxWidth)}px`,
                width: "auto",
                ...(wouldOverflowBottom
                    ? { bottom: window.innerHeight - rect.top + 4, top: "auto" }
                    : { top: rect.bottom + 4 }),
                ...(wouldOverflowRight
                    ? { right: window.innerWidth - rect.right, left: "auto" }
                    : { left: rect.left }),
            };
        };

        const isOutlined = variant === "outlined";

        return (
            <div ref={containerRef} className={cn(
                "relative",
                isOutlined
                    ? "border border-border rounded-md bg-card focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/20 transition-colors px-2"
                    : "px-1",
                containerClassName
            )}>
                <input
                    ref={ref}
                    type="text"
                    value={value ?? ""}
                    autoComplete="off"
                    placeholder={placeholder}
                    className={cn(
                        "w-full text-foreground bg-transparent",
                        "border-0 outline-none px-0",
                        "placeholder:text-muted-foreground/40 placeholder:font-normal transition-colors duration-100",
                        INPUT_SIZE_CLASSES[inputSize],
                        className
                    )}
                    onChange={(e) => {
                        onChange(e.target.value);
                        setIsOpen(!!(e.target.value.trim() || showAllOnFocus));
                    }}
                    onFocus={() => {
                        setIsFocused(true);
                        if (showAllOnFocus || (value && value.trim() && filtered.length > 0))
                            setIsOpen(true);
                    }}
                    onClick={() => {
                        if (showAllOnFocus || (value && value.trim() && filtered.length > 0))
                            setIsOpen(true);
                    }}
                    onBlur={() => {
                        setIsFocused(false);
                        setTimeout(() => setIsOpen(false), 200);
                    }}
                    onKeyDown={(e) => {
                        if (!isOpen && filtered.length > 0 && (value || showAllOnFocus)) {
                            setIsOpen(true);
                            setHighlightedIndex(0);
                            return;
                        }
                        if (!isOpen) {
                            if (e.key === "Enter" && onEnterNext) { e.preventDefault(); onEnterNext(); }
                            return;
                        }
                        if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setHighlightedIndex((p) => Math.min(p + 1, filtered.length - 1));
                        } else if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setHighlightedIndex((p) => Math.max(p - 1, 0));
                        } else if (e.key === "Enter") {
                            e.preventDefault();
                            if (filtered.length > 0 && highlightedIndex >= 0) {
                                onChange(filtered[highlightedIndex]);
                                setIsOpen(false);
                                if (onEnterNext) setTimeout(onEnterNext, 100);
                            } else if (onEnterNext) {
                                onEnterNext();
                            }
                        } else if (e.key === "Escape") {
                            setIsOpen(false);
                        } else if (e.key === "Tab") {
                            setIsOpen(false);
                        }
                    }}
                />

                {isOpen && (showAllOnFocus || filtered.length > 0) && createPortal(
                    <div
                        ref={dropdownRef}
                        className="z-[9999] bg-popover shadow-lg border border-border rounded-md max-h-48 overflow-auto text-sm ring-1 ring-border"
                        style={getDropdownStyle()}
                    >
                        {filtered.map((option, i) => (
                            <div
                                key={`${option}-${i}`}
                                ref={(el) => { optionRefs.current[i] = el; }}
                                className={cn(
                                    "px-3 py-1.5 cursor-pointer transition-colors border-b border-border/30 last:border-0 whitespace-nowrap text-xs",
                                    i === highlightedIndex
                                        ? "bg-primary text-primary-foreground"
                                        : "hover:bg-accent text-foreground"
                                )}
                                onMouseEnter={() => setHighlightedIndex(i)}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    onChange(option);
                                    setIsOpen(false);
                                    if (onEnterNext) setTimeout(onEnterNext, 100);
                                }}
                            >
                                {option}
                            </div>
                        ))}
                    </div>,
                    document.body
                )}

                {isOpen && filtered.length === 0 && value.trim() && !showAllOnFocus && createPortal(
                    <div
                        className="z-[9999] bg-popover shadow-lg border border-border rounded-md text-xs ring-1 ring-border"
                        style={getDropdownStyle()}
                    >
                        <div className="px-3 py-2 text-muted-foreground italic text-center">
                            No matches — press Enter to continue
                        </div>
                    </div>,
                    document.body
                )}
            </div>
        );
    }
);

AutocompleteInput.displayName = "AutocompleteInput";
export default AutocompleteInput;